/**
 * TopoMapControl — framework-agnostic topology map control with UI chrome.
 *
 * Mirrors the WinForms TopoMapControl: contains a MapControl (canvas rendering)
 * and adds the surrounding UI chrome — zoom slider, zoom-in/out/fit buttons,
 * and a search trigger button.
 *
 * Usage:
 *   const ctrl = new TopoMapControl(containerElement, listener);
 *   ctrl.loadMap(map, isEditable);
 *   ctrl.setVisible(true);
 *
 * The host application (e.g. an Angular component) provides a
 * TopoMapControlListener to handle events. Angular-specific chrome
 * (context menus, export dialogs, device detail panels) stays in the
 * Angular layer and is NOT part of this class.
 */
import { MapControl, MapControlListener, MapControlSettings } from './MapControl';
import { TopoMap } from '../dtos/topology/TopoMap';
import { Point } from '../dtos/topology/Point';
import { Matrix } from './Matrix';


// ── Listener ────────────────────────────────────────────────────────────────

export interface TopoMapControlListener extends MapControlListener {
    /** Fired when the user clicks the search button in the toolbar. */
    onSearchRequested?(): void;
    /** Fired when the user clicks the Navigate Up button in the toolbar. */
    onNavigateUpRequested?(): void;
}


// ── Settings ─────────────────────────────────────────────────────────────────

export interface TopoMapControlSettings extends MapControlSettings {
    // (reserved for future per-instance overrides)
}


// ── TopoMapControl ────────────────────────────────────────────────────────────

export class TopoMapControl {

    private _mapControl: MapControl;
    private _root: HTMLDivElement;
    private _zoomSlider: HTMLInputElement;
    private _upBtn: HTMLAnchorElement | null = null;

    /** True once the static CSS block has been injected into <head>. */
    private static _stylesInjected = false;

    constructor(
        container: HTMLElement,
        private readonly listener: TopoMapControlListener,
        settings?: TopoMapControlSettings
    ) {
        TopoMapControl._injectStyles();
        this._buildDOM(container, settings);
    }


    // ── DOM construction ─────────────────────────────────────────────────────

    private _buildDOM(container: HTMLElement, settings?: TopoMapControlSettings): void {

        // Root — fills the supplied container
        this._root = document.createElement('div');
        this._root.className = 'tmc-root';

        // Canvas container (drives MapControl's viewport size measurements)
        const canvasContainer = document.createElement('div');
        canvasContainer.className = 'tmc-canvas-container';

        const canvas = document.createElement('canvas');
        canvas.className = 'tmc-canvas';
        canvas.setAttribute('unselectable', 'on');
        canvasContainer.appendChild(canvas);

        // Overlay container (mouseover labels; pointer-events: none)
        const overlayContainer = document.createElement('div');
        overlayContainer.className = 'tmc-overlay-container';

        const overlay = document.createElement('canvas');
        overlayContainer.appendChild(overlay);

        // Zoom toolbar
        const toolbar = this._buildToolbar();

        this._root.appendChild(canvasContainer);
        this._root.appendChild(overlayContainer);
        this._root.appendChild(toolbar);
        container.appendChild(this._root);

        // MapControl — pass the canvas container as the viewport element
        this._mapControl = new MapControl(canvas, overlay, canvasContainer, this.listener, settings);

        // Keep zoom slider in sync with programmatic zoom changes
        this._mapControl.zoomValueChanged = (value: number) => {
            this._zoomSlider.value = String(Math.round(value * 100));
        };
    }

    private _buildToolbar(): HTMLDivElement {
        const toolbar = document.createElement('div');
        toolbar.className = 'tmc-toolbar tmc-unselectable';

        // Left spacer (grid cell 1)
        toolbar.appendChild(document.createElement('div'));

        // Centre cell — zoom controls
        const zoomTools = document.createElement('div');
        zoomTools.className = 'tmc-zoom-tools';

        // Zoom-out button
        const zoomOut = this._makeToolbarButton('Zoom Out', 'fa-search-minus');
        zoomOut.onclick = () => {
            this._mapControl.zoomAtCenter((this._zoomSlider.valueAsNumber - 10) / 100);
        };

        // Range slider
        this._zoomSlider = document.createElement('input');
        this._zoomSlider.type = 'range';
        this._zoomSlider.className = 'tmc-zoom-slider';
        this._zoomSlider.value = '100';
        this._zoomSlider.min = '5';
        this._zoomSlider.max = '200';
        this._zoomSlider.step = '5';
        this._zoomSlider.oninput = () => {
            this._mapControl.zoomAtCenter(this._zoomSlider.valueAsNumber / 100);
        };
        this._zoomSlider.onchange = this._zoomSlider.oninput;

        // Zoom-in button
        const zoomIn = this._makeToolbarButton('Zoom In', 'fa-search-plus');
        zoomIn.onclick = () => {
            this._mapControl.zoomAtCenter((this._zoomSlider.valueAsNumber + 10) / 100);
        };

        // Fit button
        const zoomFit = this._makeToolbarButton('Zoom Fit', 'fa-arrows', 'tmc-zoom-fit');
        zoomFit.onclick = () => this._mapControl.fitAndCenter();

        // Search button
        const search = this._makeToolbarButton('Search for Device on Map', 'fa-search', 'tmc-search');
        search.onclick = () => {
            if (this.listener.onSearchRequested) {
                this.listener.onSearchRequested();
            }
        };

        // Navigate-up button
        this._upBtn = this._makeToolbarButton('Navigate to Parent Group', 'fa-level-up', 'tmc-navigate-up');
        this._upBtn.onclick = () => {
            if (this.listener.onNavigateUpRequested) {
                this.listener.onNavigateUpRequested();
            }
        };

        zoomTools.appendChild(zoomOut);
        zoomTools.appendChild(this._zoomSlider);
        zoomTools.appendChild(zoomIn);
        zoomTools.appendChild(zoomFit);
        zoomTools.appendChild(search);
        zoomTools.appendChild(this._upBtn);

        toolbar.appendChild(zoomTools);

        // Right spacer (grid cell 3)
        toolbar.appendChild(document.createElement('div'));

        return toolbar;
    }

    private _makeToolbarButton(title: string, iconClass: string, extraClass?: string): HTMLAnchorElement {
        const a = document.createElement('a');
        a.className = 'tmc-toolbar-btn' + (extraClass ? ' ' + extraClass : '');
        a.title = title;
        const i = document.createElement('i');
        i.className = 'fa ' + iconClass;
        a.appendChild(i);
        return a;
    }


    // ── Static CSS injection ──────────────────────────────────────────────────

    private static _injectStyles(): void {
        if (TopoMapControl._stylesInjected) return;
        TopoMapControl._stylesInjected = true;

        const style = document.createElement('style');
        style.textContent = `
.tmc-unselectable {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
}
.tmc-root {
    position: relative;
    width: 100%;
    height: 100%;
}
.tmc-canvas-container {
    position: absolute;
    width: 100%;
    height: 94%;
    top: 0;
    background: white;
}
body.theme-dark .tmc-canvas-container {
    background: var(--blue-50);
}
.tmc-canvas {
    display: block;
    user-select: none;
    -webkit-user-select: none;
}
.tmc-overlay-container {
    overflow: hidden;
    position: absolute;
    width: 100%;
    height: 94%;
    top: 0;
    pointer-events: none;
}
.tmc-toolbar {
    background-color: var(--blue-100);
    height: 50px;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 450px 1fr;
}
.tmc-zoom-tools {
    width: 400px;
    height: 50px;
    margin: auto;
    overflow: hidden;
    padding-top: 16px;
    padding-left: 50px;
}
.tmc-toolbar-btn {
    background-image: none;
    border: 1px solid transparent;
    width: 17px;
    height: 17px;
    padding: 2px;
    display: inline-block;
    cursor: pointer;
    text-decoration: none;
    color: inherit;
}
.tmc-toolbar-btn:active { background: #a0c9ec; }
.tmc-toolbar-btn i {
    width: 16px;
    height: 16px;
    display: inline-block;
    position: relative;
    top: -4px;
}
.tmc-toolbar-btn .fa {
    color: #00245d;
}
.tmc-zoom-fit     { margin-left: 5px; }
.tmc-search       { margin-left: 30px; }
.tmc-navigate-up  { margin-left: 10px; }
.tmc-navigate-up.tmc-disabled { opacity: 0.3; pointer-events: none; }
.tmc-zoom-slider {
    -webkit-appearance: none;
    position: relative;
    width: 200px;
    height: 18px;
    margin: auto;
    padding: 0;
    background-color: --var(--blue-100);
    border: none;
    display: inline-block;
    vertical-align: middle;
}
.tmc-zoom-slider:focus { outline: none; border: none; }
.tmc-zoom-slider::-webkit-slider-runnable-track {
    width: 200px; height: 2px; background: #00245d; border: none;
}
.tmc-zoom-slider::-webkit-slider-thumb {
    -webkit-appearance: none; border: none;
    height: 18px; width: 9px; margin-top: -9px; background: #00245d;
}
.tmc-zoom-slider::-moz-range-track {
    width: 200px; height: 2px; background: #00245d; border: none;
}
.tmc-zoom-slider::-moz-range-thumb {
    border: none; height: 18px; width: 9px; background: #00245d;
}
`;
        document.head.appendChild(style);
    }


    // ── Public API — forwarded to MapControl ──────────────────────────────────

    // Toolbar state

    /** Enable or disable the Navigate Up button. */
    public setUpButtonEnabled(enabled: boolean): void {
        if (!this._upBtn) return;
        if (enabled) {
            this._upBtn.classList.remove('tmc-disabled');
            this._upBtn.title = 'Navigate to Parent Group';
        } else {
            this._upBtn.classList.add('tmc-disabled');
            this._upBtn.title = 'No Parent Group';
        }
    }

    // Map data

    public hasMap(): boolean {
        return this._mapControl.hasMap();
    }

    public getMap(): TopoMap {
        return this._mapControl.getMap();
    }

    /** Load a new map. */
    public loadMap(map: TopoMap, isEditable: boolean = false): void {
        this._mapControl.loadMap(map, isEditable);
    }

    /** Load a new map. Alias: loadMap(). */
    public setMap(map: TopoMap, isEditable: boolean): void {
        this._mapControl.setMap(map, isEditable);
    }

    public setVisible(value: boolean): void {
        this._mapControl.setVisible(value);
    }

    public get isDarkTheme(): boolean { return this._mapControl.isDarkTheme; }
    public set isDarkTheme(value: boolean) { this._mapControl.isDarkTheme = value; }

    // Zoom

    public zoomAtCenter(zoomValue: number): void {
        this._mapControl.zoomAtCenter(zoomValue);
    }

    public fitAndCenter(): void {
        this._mapControl.fitAndCenter();
    }

    // Navigation

    public showDeviceOnMap(deviceGuid: string): void {
        this._mapControl.showDeviceOnMap(deviceGuid);
    }

    public showDeviceGroupOnMap(deviceGroupGuid: string): void {
        this._mapControl.showDeviceGroupOnMap(deviceGroupGuid);
    }

    // Render

    public redraw(): void {
        this._mapControl.redraw();
    }

    // Coordinate transforms

    public viewToWorld(point: Point): Point {
        return this._mapControl.viewToWorld(point);
    }

    public worldToView(point: Point): Point {
        return this._mapControl.worldToView(point);
    }

    public createInverseTransformMatrix(): Matrix {
        return this._mapControl.createInverseTransformMatrix();
    }

    public createTransformMatrix(): Matrix {
        return this._mapControl.createTransformMatrix();
    }
}
